import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  type BrandCreate,
  type BrandUpdate,
  createBrandMutation,
  getBrandQueryKey,
  listBrandsQueryKey,
  updateBrandMutation,
} from "@/api";
import type { BrandKitValue } from "@/features/build/brands/BrandKitPreview";
import type { BrandFormState } from "@/features/build/brands/NewBrandPage";
import type { DraftView } from "../draftModel";
import { useDraftActions } from "../useDraftActions";
import { useDraftRun } from "../useDraftRun";

export type BrandDraftBody = Partial<Omit<BrandCreate, "brandKit">> & { brandKit?: BrandKitValue };

/** The create form's draft (its flat kit fields) from a Build with AI brand draft. */
function formDraft(body: BrandDraftBody): BrandFormState["draft"] {
  const palette = body.brandKit?.palette ?? {};
  return {
    name: body.name,
    description: body.description ?? undefined,
    audience: body.audience ?? undefined,
    primary: palette.primary,
    secondary: palette.secondary,
    bg: palette.background,
    text: palette.text,
    accents: palette.accents?.join(", "),
    headingFont: body.brandKit?.fonts?.heading,
    bodyFont: body.brandKit?.fonts?.body,
    tone: body.brandKit?.tone,
  };
}

/** Create (POST, linked to the chat) or Save (PUT) for a brand draft card. */
export function useBrandDraft(draft: DraftView) {
  const { t } = useTranslation("buildChat", { keyPrefix: "brand" });
  const navigate = useNavigate();
  const actions = useDraftActions();
  const create = useMutation(createBrandMutation());
  const update = useMutation(updateBrandMutation());
  const brandUuid = useRef<string | null>(draft.targetUuid);
  const body = draft.draft as BrandDraftBody;

  const run = useDraftRun(
    draft.draftId,
    [
      draft.targetUuid
        ? {
            key: "save",
            label: t("save"),
            run: async () => {
              const path = { brand_uuid: draft.targetUuid ?? "" };
              await update.mutateAsync({ path, body: body as BrandUpdate, throwOnError: true });
            },
          }
        : {
            key: "create",
            label: t("create"),
            run: async () => {
              const result = await create.mutateAsync({
                body: { ...(body as BrandCreate), sourceSessionUuid: actions.sessionUuid },
                throwOnError: true,
              });
              brandUuid.current = result.data.uuid;
            },
          },
    ],
    {
      fallback: t("failed"),
      success: draft.targetUuid ? t("saved") : t("created"),
      entityUuid: () => brandUuid.current,
      invalidate: (uuid) => [listBrandsQueryKey(), getBrandQueryKey({ path: { brand_uuid: uuid } })],
    },
  );

  return {
    body,
    ...run,
    openInForm: draft.targetUuid
      ? undefined
      : () =>
          navigate("/build/brands/new", {
            state: {
              draft: formDraft(body),
              brandStyle: body.brandStyle ?? undefined,
              sourceSessionUuid: actions.sessionUuid,
            } satisfies BrandFormState,
          }),
  };
}
